/**
 * @file schemas.ts
 * @brief Zod contracts for Personal Notes on the web panel.
 *
 * @details
 * Notes are USER-scoped, never company-scoped — every backend route filters on
 * `owner_id`, so these are always the caller's own notes even for a company
 * admin. That is the opposite of `company-meetings`, and the reason Notes is
 * its own feature module rather than a tab inside one.
 *
 * The wire shape is kept as the API sends it (snake_case, `.passthrough()`),
 * matching every other feature here — with ONE exception: `insights`, whose
 * JSON has carried both snake_case and camelCase keys since the pipeline was
 * written. Normalising it once here means the detail view renders fourteen
 * fields without writing `?? ` twice for each of them, which is exactly the
 * kind of drift that leaves half a screen silently blank.
 *
 * @dependencies
 * - zod
 *
 * @references
 * - BACKEND/src/features/notes/routes.js (the routes these mirror)
 * - FRONTEND/src/screens/Notes/notesStorage.ts (the mobile twin)
 * - docs/product/voice-notes.md
 *
 * @author DES
 * @date 21-08-2026
 */

import { z } from "zod";
import { readPointLevels } from "./pointLevels";

/**
 * Notes and agendas are different things (DES 12-10-2026): an agenda lives in
 * the library's Agendas tab and is what a meeting's agenda picker offers.
 */
export type NoteKind = "note" | "agenda";

/** How the note was made. A typed note has no audio and never ran ASR. */
export const NoteSourceSchema = z.enum(["voice", "typed"]);
export type NoteSource = z.infer<typeof NoteSourceSchema>;

export const NoteStatusSchema = z.enum([
  "uploading",
  "processing",
  "analyzing",
  "ready",
  "error",
]);
export type NoteStatus = z.infer<typeof NoteStatusSchema>;

// ----------------------------------------------------------------------
// Insights — voice notes only
//
// A typed note NEVER has these: nothing generates them and nothing offers to.
// Reading them is safe on any note; the absence is the normal state, not a
// pending one, so nothing here should render a "generating…" placeholder.
// ----------------------------------------------------------------------
const str = (v: unknown) => String(v ?? "").trim();

const strings = (v: unknown): string[] =>
  Array.isArray(v) ? v.map(str).filter(Boolean) : [];

const timed = (v: unknown, prefix: string) =>
  Array.isArray(v)
    ? v
        .map((row, i) => {
          const r = (row ?? {}) as Record<string, unknown>;
          const label = str(r.label ?? r.task ?? r.text);
          return label
            ? {
                id: str(r.id) || `${prefix}_${i + 1}`,
                label,
                when: str(r.when ?? r.date) || "—",
              }
            : null;
        })
        .filter((r): r is { id: string; label: string; when: string } => !!r)
    : [];

const checks = (v: unknown) =>
  Array.isArray(v)
    ? v
        .map((row, i) => {
          const r = (row ?? {}) as Record<string, unknown>;
          const label = str(r.label ?? r.task);
          return label
            ? {
                id: str(r.id) || `todo_${i + 1}`,
                label,
                done: !!(r.done ?? r.completed),
              }
            : null;
        })
        .filter((r): r is { id: string; label: string; done: boolean } => !!r)
    : [];

const chapters = (v: unknown) =>
  Array.isArray(v)
    ? v
        .map((row, i) => {
          const r = (row ?? {}) as Record<string, unknown>;
          const title = str(r.title ?? r.label);
          return title
            ? {
                id: str(r.id) || `ch_${i + 1}`,
                title,
                range: str(r.range ?? r.timestamp) || "—",
              }
            : null;
        })
        .filter((r): r is { id: string; title: string; range: string } => !!r)
    : [];

export type NoteInsights = {
  summary: string;
  summaryBullets: string[];
  actionItems: { id: string; label: string; done: boolean }[];
  decisions: string[];
  openQuestions: string[];
  reminders: { id: string; label: string; when: string }[];
  scheduledTasks: { id: string; label: string; when: string }[];
  deadlinesMentioned: { id: string; label: string; when: string }[];
  topics: string[];
  peopleMentioned: string[];
  keywords: string[];
  chapters: { id: string; title: string; range: string }[];
  cleanTranscript: string;
  emailDraft: string;
  bulletMemo: string[];
  translatePreview: string;
  generatedAt: string;
};

/**
 * `generated_at` is the liveness test, not the presence of the object.
 *
 * An empty `{}` is written before the pipeline runs, so treating "insights is
 * an object" as "insights exist" is how a note ends up rendering a screen of
 * empty sections. Same rule as mobile's `noteHasLiveInsights`.
 */
export function parseNoteInsights(raw: unknown): NoteInsights | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const o = raw as Record<string, unknown>;
  const generatedAt = o.generated_at ?? o.generatedAt;
  if (!generatedAt) {
    return null;
  }

  return {
    summary: str(o.summary),
    summaryBullets: strings(o.summary_bullets ?? o.summaryBullets),
    actionItems: checks(o.action_items ?? o.actionItems),
    decisions: strings(o.decisions),
    openQuestions: strings(o.open_questions ?? o.openQuestions),
    reminders: timed(o.reminders, "rem"),
    scheduledTasks: timed(o.scheduled_tasks ?? o.scheduledTasks, "task"),
    deadlinesMentioned: timed(
      o.deadlines_mentioned ?? o.deadlinesMentioned,
      "ddl",
    ),
    topics: strings(o.topics),
    peopleMentioned: strings(o.people_mentioned ?? o.peopleMentioned),
    keywords: strings(o.keywords),
    chapters: chapters(o.chapters),
    cleanTranscript: str(o.clean_transcript ?? o.cleanTranscript),
    emailDraft: str(o.email_draft ?? o.emailDraft),
    bulletMemo: strings(o.bullet_memo ?? o.bulletMemo),
    translatePreview: str(o.translate_preview ?? o.translatePreview),
    generatedAt: str(generatedAt),
  };
}

// ----------------------------------------------------------------------
// The note row
// ----------------------------------------------------------------------

/** A meeting this note is attached to, as the LIST route carries it. */
export const NoteMeetingRefSchema = z
  .object({
    meetingId: z.string(),
    title: z.string().nullable().optional(),
    role: z.enum(["prep", "addendum"]).nullable().optional(),
  })
  .passthrough();

export const VoiceNoteSchema = z
  .object({
    id: z.string(),
    title: z.string().nullable().optional(),
    language: z.string().nullable().optional(),
    // Rows predating the column are recordings — absent means 'voice'.
    source: NoteSourceSchema.nullable().optional(),
    /** 'note' | 'agenda' (DES 12-10-2026). Read through `noteKind`: a
     *  server without the column sends nothing, which means 'note'. */
    kind: z.string().nullable().optional(),
    recording_url: z.string().nullable().optional(),
    recording_size_bytes: z.coerce.number().nullable().optional(),
    duration_seconds: z.coerce.number().nullable().optional(),
    status: NoteStatusSchema.nullable().optional(),
    transcript_text: z.string().nullable().optional(),
    is_pinned: z.boolean().nullable().optional(),
    // A note is a summary and its points (ADR-025). `points` is newline-joined
    // on both kinds — the user's own on a typed note, the model's reading of
    // the transcript on a recording.
    summary: z.string().nullable().optional(),
    points: z.string().nullable().optional(),
    /** Sub-points (DES 12-10-2026): 0 / 1 per point, aligned with `points`;
     *  null = all top-level. Read through `notePointLevels`, never raw. */
    point_levels: z.unknown().nullable().optional(),
    /** Set once a PERSON edited them — regeneration refuses after this. */
    points_edited_at: z.string().nullable().optional(),
    /** The summary in the spoken language. Output is English by design, so
     *  this is the only way back to the language actually used. */
    translate_preview: z.string().nullable().optional(),
    /** @deprecated Retired by ADR-025; read only as a fallback for notes made
     *  before it, which carry neither column. */
    insights: z.unknown().nullable().optional(),
    tags: z.array(z.string()).nullable().optional(),
    // Only the LIST route carries these; detail reads them from /links.
    meeting_links: z.array(NoteMeetingRefSchema).nullable().optional(),
    created_at: z.string(),
    updated_at: z.string().nullable().optional(),
  })
  .passthrough();

export type VoiceNote = z.infer<typeof VoiceNoteSchema>;

export const NotesListResponseSchema = z
  .object({
    success: z.boolean().optional(),
    notes: z.array(VoiceNoteSchema).nullable().optional(),
    page: z.coerce.number().optional(),
    pageSize: z.coerce.number().optional(),
    total: z.coerce.number().optional(),
    hasMore: z.boolean().optional(),
  })
  .passthrough();

export type NotesListResponse = z.infer<typeof NotesListResponseSchema>;

export const NoteResponseSchema = z
  .object({
    success: z.boolean().optional(),
    note: VoiceNoteSchema,
  })
  .passthrough();

export const NoteTagsResponseSchema = z
  .object({
    success: z.boolean().optional(),
    tags: z
      .array(z.object({ tag: z.string(), uses: z.coerce.number() }))
      .nullable()
      .optional(),
  })
  .passthrough();

/** One meeting this note is attached to, from `GET /notes/:id/links`. */
export const NoteLinkSchema = z
  .object({
    meeting_id: z.string(),
    title: z.string().nullable().optional(),
    role: z.enum(["prep", "addendum"]).nullable().optional(),
    created_at: z.string().nullable().optional(),
    linked_at: z.string().nullable().optional(),
  })
  .passthrough();

export const NoteLinksResponseSchema = z
  .object({
    success: z.boolean().optional(),
    links: z.array(NoteLinkSchema).nullable().optional(),
  })
  .passthrough();

export type NoteLink = z.infer<typeof NoteLinkSchema>;

// ----------------------------------------------------------------------
// "My notes x this meeting" reports (29-09-2026 on the phone, web 06-10-2026)
// GET /notes/:id/meeting-reports lists the note's linked meetings with their
// report state; POST /notes/:id/meeting-reports/:meetingId builds one. Author
// only on the server. Kept permissive (`.passthrough()`, `.catch`) so a field
// added server-side never blanks the section.
// Related: BACKEND/src/features/notes/noteMeetingReport.service.mjs,
//          FRONTEND/src/screens/Notes/notesStorage.ts (the same types),
//          NoteMeetingReports.tsx (the web view)
// ----------------------------------------------------------------------
export const NoteReportAnchorSchema = z
  .object({
    startSecs: z.number().nullable().optional(),
    endSecs: z.number().nullable().optional(),
    segmentId: z.string().nullable().optional(),
    quote: z.string().nullable().optional(),
    confidence: z.enum(["exact", "approx", "none"]).nullable().optional().catch(null),
  })
  .passthrough();

export const NoteReportPointSchema = z
  .object({
    i: z.number(),
    point: z.string(),
    status: z.enum(["discussed", "partly", "not_discussed"]).catch("not_discussed"),
    summary: z.string().nullable().optional(),
    isQuestion: z.boolean().optional(),
    answer: z.string().nullable().optional(),
    answered: z.enum(["yes", "partly", "no"]).nullable().optional().catch(null),
    anchor: NoteReportAnchorSchema.nullable().optional(),
  })
  .passthrough();

export const NoteReportTodoSchema = z
  .object({
    id: z.string().nullable().optional(),
    task: z.string(),
    dueDate: z.string().nullable().optional(),
    done: z.boolean().optional(),
    team: z.boolean().optional(),
    anchor: NoteReportAnchorSchema.nullable().optional(),
  })
  .passthrough();

export const NoteMeetingReportSchema = z
  .object({
    meetingTitle: z.string().nullable().optional(),
    points: z.array(NoteReportPointSchema).default([]),
    counts: z
      .object({
        total: z.number(),
        discussed: z.number(),
        partly: z.number(),
        notDiscussed: z.number().optional(),
      })
      .passthrough(),
    myTodos: z.array(NoteReportTodoSchema).default([]),
    generatedAt: z.string().nullable().optional(),
  })
  .passthrough();

export const NoteMeetingReportEntrySchema = z
  .object({
    meetingId: z.string(),
    title: z.string().nullable().optional(),
    startedAt: z.string().nullable().optional(),
    meetingReady: z.boolean(),
    status: z.enum(["none", "pending", "ready", "failed"]).catch("none"),
    stale: z.boolean().optional(),
    report: NoteMeetingReportSchema.nullable().optional(),
    recordingUrl: z.string().nullable().optional(),
    error: z.string().nullable().optional(),
  })
  .passthrough();

export const NoteMeetingReportsResponseSchema = z
  .object({
    success: z.boolean().optional(),
    note: z.object({ pointCount: z.number().optional() }).passthrough().nullable().optional(),
    meetings: z.array(NoteMeetingReportEntrySchema).nullable().optional(),
  })
  .passthrough();

export const NoteMeetingReportResponseSchema = z
  .object({
    success: z.boolean().optional(),
    report: NoteMeetingReportSchema,
  })
  .passthrough();

export type NoteReportAnchor = z.infer<typeof NoteReportAnchorSchema>;
export type NoteReportPoint = z.infer<typeof NoteReportPointSchema>;
export type NoteMeetingReport = z.infer<typeof NoteMeetingReportSchema>;
export type NoteMeetingReportEntry = z.infer<typeof NoteMeetingReportEntrySchema>;

// ----------------------------------------------------------------------
// Params & helpers
// ----------------------------------------------------------------------
export type NotesListParams = {
  page?: number;
  pageSize?: number;
  /** Server-side: covers title, transcript, tags, insights text AND dates. */
  search?: string;
  /** Notes or agendas only (DES 12-10-2026). Omitted = every note. */
  kind?: NoteKind;
};

/** 'agenda' or 'note'; anything else (or nothing) is a note. */
export function noteKind(note: Pick<VoiceNote, "kind">): NoteKind {
  return note.kind === "agenda" ? "agenda" : "note";
}

/**
 * The note's points. BOTH kinds have them now (ADR-025).
 *
 * The fallback is for typed notes written before `points` existed, whose lines
 * really are newline-separated in `transcript_text` because that is how they
 * were typed. A recording never falls back: its `transcript_text` is the
 * transcript, and splitting that would produce one enormous pseudo-point.
 */
export function notePoints(note: VoiceNote): string[] {
  const source =
    note.points && note.points.trim()
      ? note.points
      : note.source === "typed"
        ? (note.transcript_text ?? "")
        : "";
  return String(source)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

/**
 * Sub-points (DES 12-10-2026): the level of each of `notePoints(note)`, or
 * null when the note is flat or the map does not line up with its points.
 */
export function notePointLevels(note: VoiceNote, points = notePoints(note)): number[] | null {
  return readPointLevels(note.point_levels, points.length);
}

/** What the note is about. Falls back to a pre-ADR-025 recording's insights. */
export function noteSummary(note: VoiceNote): string {
  const own = String(note.summary ?? "").trim();
  if (own) {
    return own;
  }
  const legacy = (note.insights ?? {}) as Record<string, unknown>;
  return String(legacy.summary ?? "").trim();
}
